import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Sparkles } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import useWin98Scope from '../hooks/useWin98Scope';

const LoginPage = () => {
  const { login, googleLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  useWin98Scope();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Brand mark on the desktop */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3">
            <div className="w-12 h-12 bg-[var(--win-titlebar-start)] flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">KyberCosplay</h1>
          </div>
        </div>

        {/* Login window */}
        <div className="win-window">
          <div className="win-titlebar">
            <span>Sign In</span>
          </div>
          <div className="p-6">
            <p className="text-center text-black mb-4">Welcome back, cosplayer!</p>

            {/* Shared-account notice */}
            <div className="win-raised px-4 py-3 mb-4 text-center">
              <p className="text-xs text-black">
                Have a{' '}
                <a
                  href="https://mando.kybersuite.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--win-titlebar-start)] underline"
                >
                  MandoMaster
                </a>{' '}
                account? Same email &amp; password works here — or use Google Sign-In.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-black">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-black">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="login-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="h-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-black"
                    style={{ border: 'none', boxShadow: 'none', background: 'transparent', padding: 0 }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-[var(--win-titlebar-start)] underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="w-full h-12 win-btn-accent win-btn-default font-semibold"
              >
                {loading ? 'Signing In…' : 'Sign In'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full win-divider" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-[var(--win-face)] text-black">or</span>
              </div>
            </div>

            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={async (creds) => {
                  try {
                    await googleLogin(creds.credential);
                    toast.success('Welcome!');
                  } catch {
                    toast.error('Google sign-in failed');
                  }
                }}
                onError={() => toast.error('Google sign-in failed')}
                theme="filled_black"
                shape="rectangular"
                width="320"
              />
            </div>

            <div className="mt-6 text-center">
              <p className="text-black">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="text-[var(--win-titlebar-start)] underline font-medium"
                  data-testid="register-link"
                >
                  Sign Up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
